import { Component, inject, input } from "@angular/core";

import { MatListModule } from "@angular/material/list";
import { MatIconModule } from "@angular/material/icon";
import { IWorkflow } from "../../../automation/interfaces/i-workflow";
import { IInstructions, IInstructionsPreview } from "../../../automation/interfaces/i-instructions";
import { KENDO_LISTVIEW, ListViewModule } from "@progress/kendo-angular-listview";
import { MatButtonModule } from "@angular/material/button";
import { MatBottomSheet, MatBottomSheetConfig } from "@angular/material/bottom-sheet";
import { PromptLibraryListItemSummaryComponent } from "./prompt-library-list-item-summary/prompt-library-list-item-summary.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import { UtilitiesService } from "../../../../services/utilities.service";
import { PromptLibraryService } from "../../services/prompt-library.service";
import { MatMenuModule } from "@angular/material/menu";
import { ApiService } from "../../../../services/api.service";
import { ISensitivityLabel } from "../../../../interfaces/i-sensitivity-label";
import { IGroup, IUser } from "../../../../interfaces/i-user-profile";
import { AutomationUtilitiesService } from "../../services/automation-utilities.service";
import { AgentService } from "../../../agent/services/agent.service";
import { AutomationStateService } from "../../services/automation-state.service";
import { AutomationExecutionService } from "../../services/automation-execution.service";
import { InstructionType } from "../../../../enums/automation.enum";
import { ILibraryItem } from "../interfaces/i-library-item";
import { SpinnerService } from "../../../../shared/spinner/spinner.service";
import { ChatSessionService } from "../../services/chat-session.service";
import { MultiSessionService } from "../../../../services/multi-session.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { AlertEnum } from "../../../../shared/alert/alert.enum";
import { ProfileService } from "../../../../services/profile.service";
import { UiLayoutService } from "../../../../ui-layout/services/ui-layout.service";

/**
 * Component to display the list of prompts in the library
 */
@Component({
  selector: "app-prompt-library-list",
  imports: [
    MatListModule,
    MatIconModule,
    MatButtonModule,
    ListViewModule,
    MatTooltipModule,
    MatMenuModule,
    KENDO_LISTVIEW
],
  templateUrl: "./prompt-library-list.component.html",
  styleUrls: ["./prompt-library-list.component.scss"],
})
export class PromptLibraryListComponent {
  data = input<ILibraryItem[]>();
  title = input<string>("");
  isAutomationDisabled = input<boolean>(false);
  tooltipText = 'Click to run: ';

  #bottomSheet = inject(MatBottomSheet);
  #router = inject(Router);
  #utilitiesService = inject(UtilitiesService);
  #promptLibraryService = inject(PromptLibraryService);
  #automationStateService = inject(AutomationStateService);
  #automationExecutionService = inject(AutomationExecutionService);
  #automationUtilitiesService = inject(AutomationUtilitiesService);
  #apiService = inject(ApiService);
  #profileService = inject(ProfileService);
  #spinnerService = inject(SpinnerService);
  #chatSessionService = inject(ChatSessionService);
  #multiSessionService = inject(MultiSessionService);
  #alertService = inject(AlertService);
  #uiLayoutService = inject(UiLayoutService);

  ngOnInit(): void {
    if (this.title() != "Workflows" && this.title() != "Agents") {
      this.tooltipText = 'Click to insert: ';
    }

  }

  /**
   * Navigates the user to the Edit Instruction or Workflow page
   * @param libraryItem The library item to be edited
   */
  editPrompt(libraryItem: ILibraryItem) {
    const prompt = libraryItem.item;
    this.#utilitiesService.setDefaultLocationFrom();
    if (libraryItem.type === 'instruction') {
      this.#router.navigateByUrl(`/edit-instruction/${prompt.id}`);
    } else {
      this.#router.navigateByUrl(`/edit-workflow/${prompt.id}`);
    }
  }

  /**
   * Displays a summary of the prompt in a popup
   * @param libraryItem The library item to be summarized
   */
  showPromptSummary(libraryItem: ILibraryItem) {
    this.#openBottomSheet(libraryItem.item);
  }

  /**
   * Opens a bottom sheet to display details of an instruction or workflow
   * @param prompt The instruction or workflow to display
   */
  #openBottomSheet(prompt: IInstructions | IWorkflow) {
    interface BottomSheetData {
      meta: {
        name?: string;
        description?: string;
        keywords?: string[] | string;
        topP?: number;
        temperature?: number;
        model?: string;
        prompt?: string;
        type?: string;
        agent?: string | any;
        fileUploadLabel?: string;
        collectInfoLabel?: string;
        collectInformationLabel?: string;
        sensitivityLabels?: ISensitivityLabel[];
        users?: IUser[];
        owners?: IUser[];
        groups?: IGroup[];
        isUniversal?: boolean;
        isEnabled?: boolean;
        instructions?: (string | IInstructions)[];
      };
    }

    const isWorkflow = this.title() === "Workflows";

    if (isWorkflow) {
      // Handle workflow prompts
      this.#handleWorkflowPrompt(prompt as IWorkflow);
    } else {
      // Handle instruction prompts
      this.#automationUtilitiesService.handleInstructionPrompt(prompt as IInstructions);
    }
  }

  /**
   * Handles displaying workflow details in a bottom sheet
   * @param workflowPrompt The workflow to display
   */
  #handleWorkflowPrompt(workflowPrompt: IWorkflow): void {
    // Get instruction IDs from the workflow
    const instructionIds = workflowPrompt.details?.instructions || [];

    // Initialize data with available workflow details
    const data = {
      meta: {
        name: workflowPrompt.details?.name,
        description: workflowPrompt.details?.description,
        instructions: [] as any[], // Will be populated with instruction objects or names
        keywords: workflowPrompt.details?.keywords || [],
        sensitivityLabels: workflowPrompt.details?.sensitivityLabels || [],
        users: workflowPrompt.details?.users || [],
        owners: workflowPrompt.details?.owners || [],
        groups: workflowPrompt.details?.groups || [],
        isEnabled: workflowPrompt.details?.isEnabled,
        isUniversal: workflowPrompt.details?.isUniversal,
      }
    };

    // If no instructions, just open the bottom sheet
    if (instructionIds.length === 0) {
      this.#automationUtilitiesService.openBottomSheetWithData(data);
      return;
    }

    // Show spinner while fetching instruction details
    this.#spinnerService.show();

    // Fetch instruction details if IDs are available
    this.#apiService.getInstructionsById(instructionIds).subscribe({
      next: (instructions: IInstructionsPreview[]) => {
        if (instructions?.length) {
          // Create formatted instruction strings with name and description if available
          data.meta.instructions = instructions.map(instruction => {
            const name = instruction.name || 'Unnamed Instruction';
            return name;
          });
        }
        this.#automationUtilitiesService.openBottomSheetWithData(data);
        this.#spinnerService.hide(); // Hide spinner on success
      },
      error: (error: Error) => {
        console.error('Error fetching instructions for workflow:', error);
        // If error occurs, at least show the instruction IDs
        data.meta.instructions = instructionIds.map(id => `Instruction ID: ${id}`);
        this.#automationUtilitiesService.openBottomSheetWithData(data);
        this.#spinnerService.hide(); // Hide spinner on error
      }
    });
  }

  /**
   * Runs the prompt
   * @param libraryItem The library item to be run
   */
  runPrompt(libraryItem: ILibraryItem): void {
    // Don't run if the item is disabled
    if (this.isAutomationDisabled() || libraryItem.isDisabled) {
      return;
    }

    if (this.#multiSessionService.hasReachedMaxConcurrency()) {
      this.#alertService.showAlert({
        message: 'Maximum concurrent sessions reached. Please wait for an active session to complete before starting a new one.',
        type: AlertEnum.error,
      });
      return;
    }

    const prompt = libraryItem.item;

    // Use the Automation Library service to set the selected prompt
    if (libraryItem.type === 'instruction') {
      const instructionPrompt = prompt as IInstructions;
      const sessionId = this.#chatSessionService.selectedSessionId();

      // Set instruction in both global signal and session-specific state to avoid race conditions
      this.#automationStateService.instructionInProgress.set(instructionPrompt);
      this.#automationStateService.updateSessionState(sessionId, {
        instructionInProgress: instructionPrompt
      });

      // If Instruction type != Agent or AI Prompt, then we sort the session in the left nav with the session we ran the Automation in to be at the top.
      if (instructionPrompt.details?.type != InstructionType.Agent && instructionPrompt.details?.type != InstructionType.AiPrompt) {
        const navItems = this.#uiLayoutService.navItems();
        this.#uiLayoutService.updateAndSortChatSessionsInLeftNav(String(sessionId), navItems, true);
      }

      if (instructionPrompt.details.type === InstructionType.CollectInformation) {
        this.#automationUtilitiesService.openInstructionUserInputDialog(false, sessionId);
      } else if (instructionPrompt.details.type === InstructionType.FileUpload) {
        if (!this.#automationStateService.isWorkflowInProgress()) {
          this.#promptLibraryService.setSelectedPromptForCurrentSession(instructionPrompt.details?.prompt || '', instructionPrompt);
        }
        this.#automationUtilitiesService.openInstructionUserFileUploadDialog(false, false, sessionId);
      } else {
        this.#promptLibraryService.setSelectedPromptForCurrentSession(instructionPrompt.details?.prompt || '', instructionPrompt);
      }
    } else {
      this.#automationExecutionService.startWorkflowExecution(prompt as IWorkflow, this.#chatSessionService.selectedSessionId());
    }

    // Reset the bottom sheet if it's open
    this.#bottomSheet.dismiss();
  }

  /**
   * Determines if the Edit button should be shown based on the user permissions
   * @returns True if the Edit button should be shown, false otherwise
   */
  showEditAutomationButton(automation: ILibraryItem): boolean {
    const currentUser = this.#profileService.userProfile()!;
    const isAdmin = currentUser.config?.isAdmin;
    const canManage = currentUser.config?.canManageWorkflows;

    // Looks through the list of owners of the automation and checks if the current user is an owner
    const isOwner = automation.item.details.owners?.some(owner => owner.userId === currentUser.profile.email);

    const shouldShowEdit = isAdmin || canManage || isOwner;

    return shouldShowEdit;
  }
}
